import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';
import { NoNavLayout, DashboardLayout } from 'src/layouts/dashboard';

import RequireAuth from './components/RequireAuth';
import ExternalRedirect from './components/ExternalRedirect';

/**
 * Pages don't declare an `id` prop themselves; RequireAuth reads it off the
 * routed element (`element.props.id`) to check access, so the lazy wrappers
 * are typed to accept it.
 */
type Page = React.LazyExoticComponent<React.ComponentType<{ id?: string }>>;

export const McsPage: Page = lazy(() => import('../pages/mcs'));
export const DetailPage: Page = lazy(() => import('../pages/details'));
export const LoginPage: Page = lazy(() => import('../pages/login'));
export const RegisterPage: Page = lazy(() => import('../pages/register'));
export const RedirectPage: Page = lazy(() => import('../pages/redirect'));
export const PatientsPage: Page = lazy(() => import('../pages/patients'));
export const Page404: Page = lazy(() => import('../pages/page-not-found'));
export const ScreeningPage: Page = lazy(() => import('../pages/screening'));
export const InvitesPage: Page = lazy(() => import('../pages/invites'));
export const ResetPage: Page = lazy(() => import('../pages/reset'));
export const InstructionsPage: Page = lazy(() => import('../pages/instructions'));
export const ReviewsPage: Page = lazy(() => import('../pages/review'));
export const DemoPage: Page = lazy(() => import('../pages/demo'));
export const TutorPage: Page = lazy(() => import('../pages/tutor'));
export const ChatPage: Page = lazy(() => import('../pages/chat'));

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    {
      element: (
        <DashboardLayout>
          <Suspense>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      ),
      children: [
        { path: 'patients', element: <RequireAuth element={<PatientsPage id="patients" />} /> },
        { path: 'screening', element: <RequireAuth element={<ScreeningPage id="screening" />} /> },
        { path: 'reviews', element: <RequireAuth element={<ReviewsPage id="reviews" />} /> },
        {
          path: 'details/:patientId',
          element: <RequireAuth element={<DetailPage id="details" />} />,
        },
        {
          path: 'screening/:patientId',
          element: <RequireAuth element={<DetailPage id="screening" />} />,
        },
        {
          path: 'reviews/:patientId',
          element: <RequireAuth element={<DetailPage id="reviews" />} />,
        },
        { path: 'invites', element: <RequireAuth element={<InvitesPage id="invites" />} /> },
        {
          path: 'instructions/staff/about?',
          element: <RequireAuth element={<InstructionsPage id="instructions" />} />,
        },
        { path: 'demo', element: <RequireAuth element={<DemoPage id="demo" />} /> },
        { path: 'chat', element: <RequireAuth element={<ChatPage id="chat" />} /> },
      ],
    },
    {
      element: (
        <NoNavLayout>
          <Suspense>
            <Outlet />
          </Suspense>
        </NoNavLayout>
      ),
      children: [
        { index: true, element: <RequireAuth element={null} /> },
        {
          path: 'patients/redirect/',
          element: <RequireAuth element={<RedirectPage id="redirect" />} />,
        },
        { path: ':lang?/login', element: <LoginPage /> },
        { path: ':lang?/register/:preProject?', element: <RegisterPage /> },
        { path: ':lang?/password/reset?/confirm?/:uid?/:token?', element: <ResetPage /> },
      ],
    },
    { path: 'tutor', element: <TutorPage /> },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
    {
      path: 'app',
      element: <ExternalRedirect href={`https://app${import.meta.env.DEV ? '-test' : ''}.megi.ai`} />,
    },
  ]);

  return routes;
}
